import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ModifierCategory } from "src/interfaces/modifier.interface";
import { RootState } from "src/store";
import { useModifierCategoriesByStoreCorrelationId } from "src/features/ModifierCategories/hooks/useModifierCategoriesByStoreCorrelationId";
import { useDeleteModifierCategory } from "src/features/ModifierCategories/hooks/useDeleteModifierCategory";
import Spinner from "src/components/common/Spinner";
import DeleteItemModal from "src/components/common/DeleteItemModal";
import ManageModifierCategoryModal from "src/features/ModifierCategories/components/ManageModifierCategoryModal";
import ModifierCategoriesHeader from "src/features/ModifierCategories/components/ModifierCategoriesHeader";
import ModifierCategoriesTable from "src/features/ModifierCategories/components/ModifierCategoriesTable";
import { search_modifier_categories } from "src/store/features/modifier-categories-slice";
import { MENU_LIST_FILTER_TABS, type MenuListActiveFilter } from "src/constants/menuListActiveFilter";

const ModifierCategoriesPage = () => {
  const [modifierCategoryToEdit, setModifierCategoryToEdit] = useState<ModifierCategory | null>(null);
  const [isModifierCategoryModalOpen, setIsModifierCategoryModalOpen] = useState(false);
  const searchTermRef = useRef("");
  const [activeFilter, setActiveFilter] = useState<MenuListActiveFilter>("ACTIVE");
  const dispatch = useDispatch();

  const { filteredModifierCategories, toBeDeletedModifierCategoryId } = useSelector((state: RootState) => state.modifierCategories);

  const { isModifierCategoriesLoading, fetchModifierCategoriesByStoreCorrelationId } = useModifierCategoriesByStoreCorrelationId();
  const { handleCloseDeleteModal, deleteModifierCategoryMutation, isDeletingModifierCategory, handleOpenDeleteModal } = useDeleteModifierCategory();

  const handleSearch = useCallback(
    (value: string) => {
      searchTermRef.current = value;
      dispatch(search_modifier_categories(value));
    },
    [dispatch]
  );

  const handleAddCategory = useCallback(() => {
    setModifierCategoryToEdit(null);
    setIsModifierCategoryModalOpen(true);
  }, []);

  const handleEditModifierCategory = useCallback((modifierCategory: ModifierCategory) => {
    setModifierCategoryToEdit(modifierCategory);
    setIsModifierCategoryModalOpen(true);
  }, []);

  const handleCloseModifierCategoryModal = useCallback(() => {
    setIsModifierCategoryModalOpen(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await fetchModifierCategoriesByStoreCorrelationId(activeFilter);
      if (!cancelled) {
        dispatch(search_modifier_categories(searchTermRef.current));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchModifierCategoriesByStoreCorrelationId, activeFilter, dispatch]);

  const statusFilterSlot = (
    <div
      className="flex flex-wrap items-center gap-1 rounded-lg border border-[#F1F1F2] p-0.5 bg-white w-full sm:w-fit"
      role="group"
      aria-label="Filter modifier categories by status"
    >
      {MENU_LIST_FILTER_TABS.map(({ id, label }) => {
        const selected = activeFilter === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => setActiveFilter(id)}
            className={`h-[38px] px-3 text-xs font-medium rounded-md transition-all outline-none focus-visible:ring-2 focus-visible:ring-[#FD7657]/40 flex-1 sm:flex-none min-w-0 ${
              selected
                ? "bg-[#FD7657] text-white shadow-sm"
                : "bg-white text-[#6b7280] hover:bg-[#FEF5F4] hover:text-[#FD7657]"
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="w-full flex-1 flex flex-col min-h-0 min-w-0">
      <div className="flex w-full min-w-0 shrink-0 overflow-visible rounded-3xl border-b border-[#e5e7eb] bg-white px-4 py-3 mb-4">
        <ModifierCategoriesHeader
          statusFilterSlot={statusFilterSlot}
          onSearchChange={handleSearch}
          trailingSlot={
            <button
              type="button"
              onClick={handleAddCategory}
              className="h-9 w-full min-w-0 rounded-lg px-3 text-sm font-medium text-white bg-[#FD7657] hover:bg-[#e45e41] transition-colors sm:w-auto sm:px-4"
            >
              Add category
            </button>
          }
        />
      </div>

      {isModifierCategoriesLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : filteredModifierCategories.length === 0 ? (
        <p className="text-[#6b7280] py-12 text-center text-sm">No modifier categories. Click &quot;Add category&quot; to create one.</p>
      ) : (
        <div className="flex-1 min-h-0 min-w-0 flex flex-col">
          <ModifierCategoriesTable
            categories={filteredModifierCategories}
            onEdit={handleEditModifierCategory}
            onDelete={handleOpenDeleteModal}
          />
        </div>
      )}

      <ManageModifierCategoryModal
        modifierCategoryToEdit={modifierCategoryToEdit}
        isModifierCategoryModalOpen={isModifierCategoryModalOpen}
        handleCloseModifierCategoryModal={handleCloseModifierCategoryModal}
      />

      <DeleteItemModal
        itemType="Category"
        isDeletingItem={isDeletingModifierCategory}
        isOpen={!!toBeDeletedModifierCategoryId}
        onClose={handleCloseDeleteModal}
        onDelete={deleteModifierCategoryMutation}
      />
    </div>
  );
};

export default ModifierCategoriesPage;
